<template>
  <div style="height: 100%;">
    <header class="chat-header">
      <div class="header-title">AI助手</div>
      <button class="header-btn" @click="showFuncModal = true">
        选择功能
      </button>
    </header>
    <div v-if="showFuncModal" class="modal-mask" @click="showFuncModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <span>快速功能选择</span>
          <button class="close-btn" @click="showFuncModal = false">×</button>
        </div>
        <!-- 从assistant.vue复制的快速功能卡片 -->
        <div class="card">
          <div
            v-for="(item, index) in quickFuncList"
            :key="index"
            class="func-item"
            :class="getFuncBgClass(index)"
            @click="handleFuncClick(item)">
            <img :src="item.agentUrl || item.image || item.img || getDefaultIcon(index)" alt="">
            <div>
              <div>{{ item.agentName || item.name }}</div>
              <span>{{ item.agentDesc || item.description }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 初始化加载动画 -->
    <div v-if="isInitializing" class="initializing-loader">
      <div class="loader-content">
        <div class="loader-icon">
          <img :src="botAvatar" alt="AI助手" />
        </div>
        <div class="loader-text">正在初始化...</div>
        <div class="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>

    <!-- 变量配置选择界面 -->
    <div v-else-if="showVariableConfigPanel" class="variable-config-panel">
      <div class="config-panel-content">
        <!-- 图标和标题区域 -->
        <div class="config-panel-header">
          <div class="header-icon">
            <img :src="botAvatar" alt="AI助手" />
          </div>
          <div class="header-title">{{ variableConfigDialogTitle }}</div>
        </div>

        <!-- 配置项列表 -->
        <div class="config-items">
          <div v-for="(config, index) in variableConfigs" :key="index" class="config-item">
            <!-- 标签组 -->
            <div class="label-group">
              <div class="label-tag" v-if="config.category">{{ config.category }}</div>
              <div class="label-main">
                <span>{{ config.label || config.name }}</span>
                <Icon v-if="config.description || config.tooltip" type="ios-information-circle" class="info-icon"
                      @click.native="showConfigTooltip(config)" />
              </div>
            </div>
            <!-- 输入控件 -->
            <div class="input-wrapper">
              <Select v-if="config.variableType === 'Enum' || config.options"
                      v-model="variableConfigValues[config.key || config.name]" :placeholder="config.placeholder || '请选择'"
                      filterable clearable>
                <Option v-for="option in config.enumValues" :key="option" :value="option">
                  {{ option }}
                </Option>
              </Select>
              <Input v-else v-model="variableConfigValues[config.key || config.name]"
                     :placeholder="config.placeholder || '请输入'" :type="config.type || 'text'" />
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="config-panel-footer">
          <button class="button" type="primary" shape="circle" :disabled="!canSubmitVariableConfig"
                  @click="handleVariableConfigSubmit">
            <Icon type="ios-chatbubbles" style="margin-right: 4px;" />
            开始会话
          </button>
        </div>
      </div>
    </div>
    <div v-else class="ai-chat-container" :class="{ 'full-screen': isFullScreen }">

      <!-- 聊天内容区域 -->
      <div class="chat-body" ref="chatBody">
        <!-- 欢迎消息 -->
        <div class="welcome-message">
          <div class="welcome-content">
            <img :src="welcomeAvatar" alt="AI助手" class="welcome-avatar" />
            <div class="welcome-content-inner">
              <span>您好!</span>
              <h3>{{ welcomeTitle }}</h3>
            </div>
            <div class="quick-questions" v-if="welcomeQuickQuestions && welcomeQuickQuestions.length > 0">
              <div class="quick-questions-inner">
                <p>{{ welcomeDescription }}</p>
              </div>
              <button v-for="(question, index) in welcomeQuickQuestions" :key="index" class="quick-question-btn"
                      @click="sendQuickQuestion(question)">
                {{ question }}
              </button>
            </div>
          </div>
        </div>

        <!-- 消息列表 -->
        <div class="messages-container">
          <div v-for="(message, index) in messages" :key="index" class="message-item" :class="{
            'user-message': message.role === 'user',
            'bot-message': message.role === 'bot',
            'system-message': message.role === 'system'
          }">
            <!-- 消息头像 -->
            <div class="message-avatar">
              <img :src="message.role === 'user' ? currentUserAvatar : robotIcon"
                   :alt="message.role === 'user' ? '用户' : 'AI助手'" />
            </div>

            <!-- 消息内容 -->
            <div class="message-content">
              <!-- 消息头部 -->
              <div class="message-header">
                <span class="sender-name">
                  {{ message.role === 'user' ? currentUserName : botName }}
                </span>
                <span class="message-time">
                  {{ formatTime(message.timestamp) }}
                </span>
              </div>

              <!-- 消息主体 -->
              <div class="message-body">
                <!-- 文本消息 -->
                <div v-if="message.type === 'text'" class="text-message" v-html="renderMessage(message.content)"></div>

                <!-- 代码消息 -->
                <div v-if="message.type === 'code'" class="code-message">
                  <div class="code-header">
                    <span class="code-language">{{ message.language }}</span>
                    <button class="copy-btn" @click="copyCode(message.content)">
                      复制代码
                    </button>
                  </div>
                  <pre><code>{{ message.content }}</code></pre>
                </div>

                <!-- 加载消息 -->
                <div v-if="message.type === 'loading'" class="loading-message">
                  <div class="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>

                <!-- 错误消息 -->
                <div v-if="message.type === 'error'" class="error-message">
                  <div class="error-content">
                    <i class="icon-error"></i>
                    <span>{{ message.content }}</span>
                    <button v-if="message.retryable" class="retry-btn" @click="retryMessage(message)">
                      重试
                    </button>
                  </div>
                </div>
              </div>

              <!-- 消息操作 -->
              <div class="message-actions" v-if="message.role === 'bot' && message.type === 'text'">
                <button class="action-btn" @click="copyText(message.content)" title="复制">
                  <i class="icon-copy"></i>
                </button>
                <button class="action-btn" @click="regenerateMessage(message)" title="重新生成">
                  <i class="icon-regenerate"></i>
                </button>
                <button class="action-btn" @click="toggleMessageLike(message)" :title="message.liked ? '取消点赞' : '点赞'">
                  <i :class="['icon-like', { liked: message.liked }]"></i>
                  <span v-if="message.likeCount > 0">{{ message.likeCount }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- 正在输入指示器 -->
          <div v-if="isTyping" class="typing-indicator-wrapper">
            <div class="message-item bot-message">
              <div class="message-avatar">
                <img :src="botAvatar" alt="AI助手" />
              </div>
              <div class="message-content">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 输入区域 -->
      <div class="chat-footer">
        <!-- 工具栏 -->
        <div class="toolbar">
          <button class="toolbar-btn" @click="toggleAttachments" title="附件">
            <i class="icon-attachment"></i>
          </button>
          <button class="toolbar-btn" @click="insertEmoji" title="表情">
            <i class="icon-emoji"></i>
          </button>
          <button class="toolbar-btn" @click="toggleHistory" title="历史记录">
            <i class="icon-history"></i>
          </button>
        </div>

        <!-- 附件区域 -->
        <div v-if="showAttachments" class="attachments-panel">
          <div class="attachments-header">
            <h4>添加附件</h4>
            <button class="close-attachments" @click="showAttachments = false">
              <i class="icon-close"></i>
            </button>
          </div>
          <div class="attachments-content">
            <label class="file-upload-btn">
              <input type="file" multiple @change="handleFileUpload" accept=".txt,.pdf,.doc,.docx,.jpg,.jpeg,.png" />
              <i class="icon-upload"></i>
              <span>上传文件</span>
            </label>
            <div v-if="attachments.length > 0" class="attachments-list">
              <div v-for="(file, index) in attachments" :key="file.id || index" class="attachment-item">
                <span class="file-name">{{ file.name }}</span>
                <div v-if="file.uploading" class="upload-progress">
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: file.progress + '%' }"></div>
                  </div>
                  <span class="progress-text">{{ file.progress }}%</span>
                </div>
                <span v-else-if="file.error" class="upload-error">{{ file.error }}</span>
                <span v-else-if="file.url" class="upload-success">✓</span>
                <button class="remove-attachment" @click="removeAttachment(index)" :disabled="file.uploading">
                  <i class="icon-close"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 输入框 -->
        <div class="input-container">
          <div class="input-wrapper">
            <textarea ref="inputTextarea" v-model="inputText" :placeholder="inputPlaceholder"
                      @keydown.enter.exact.prevent="sendMessage" @keydown.enter.shift.exact="handleShiftEnter"
                      @input="handleInput" @focus="handleFocus" @blur="handleBlur" rows="1" class="message-input"></textarea>

            <!-- 发送按钮 -->
            <button class="send-btn" :class="{ 'disabled': !canSend }" @click="sendMessage" :disabled="!canSend">
              发送
            </button>
          </div>

          <!-- 底部状态栏 -->
          <!-- <div class="footer-status">
          <span class="char-count">{{ inputText.length }}/{{ maxLength }}</span>
          <span v-if="isConnected" class="connection-status connected">
            <i class="icon-connected"></i>
            已连接
          </span>
          <span v-else class="connection-status disconnected">
            <i class="icon-disconnected"></i>
            连接中...
          </span>
        </div> -->
        </div>
      </div>

      <!-- 历史记录侧边栏 -->
      <transition name="slide-right">
        <div v-if="showHistory" class="history-sidebar">
          <div class="sidebar-header">
            <h3>聊天历史</h3>
            <button class="close-sidebar" @click="showHistory = false">
              <i class="icon-close"></i>
            </button>
          </div>
          <div class="sidebar-content">
            <div v-for="(history, index) in chatHistory" :key="history.id || history.conversationID || index"
                 class="history-item"
                 :class="{ 'active': (history.id === conversationID) || (history.conversationID === conversationID) }"
                 @click="loadChatHistory(history)">
              <div class="history-preview">
                <div class="history-title">{{ history.title }}</div>
                <div class="history-time">{{ formatDate(history.createdAt) }}</div>
              </div>
              <button class="delete-history" @click.stop="deleteChatHistory(history.id,history.appConversationID)">
                <i class="icon-delete"></i>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { session, cookie } from '@/libs/location'
import robot from '@/assets/images/common/robot.png'
import robotIcon from '@/assets/images/common/robot-icon.png'
import defaultAvatar from '@/assets/images/avator.png'
export default {
  name: 'AIChatBox',
  props: {
    userName: {
      type: String,
      default: '用户'
    },
    userAvatar: {
      type: String,
      default: '/images/user-avatar.png'
    },

    // 初始消息
    initialMessages: {
      type: Array,
      default: () => []
    },

    // 配置项
    maxLength: {
      type: Number,
      default: 2000
    },
    autoScroll: {
      type: Boolean,
      default: true
    },
    showWelcome: {
      type: Boolean,
      default: true
    },

    // 快速问题
    quickQuestions: {
      type: Array,
      default: () => [
        '你好，请介绍一下自己',
        '如何学习编程？',
        '帮我写一个简单的Python程序',
        '解释一下什么是人工智能'
      ]
    },
    // 状态
    isConnected: {
      type: Boolean,
      default: true
    },
    isTyping: {
      type: Boolean,
      default: false
    },
    // API 配置
    apiUrl: {
      type: String,
      default: '/sso/hiAgent/chatQueryV2ByStream'
    },
    createConversationUrl: {
      type: String,
      default: '/sso/hiAgent/createConversation'
    },
    getAppConfigUrl: {
      type: String,
      default: '/sso/hiAgent/getAppConfigPreview'
    },
    getConversationListUrl: {
      type: String,
      default: '/sso/hiAgent/getConversationList'
    },
    getConversationMessagesUrl: {
      type: String,
      default: '/sso/hiAgent/getConversationMessages'
    },
    deleteConversationUrl: {
      type: String,
      default: '/sso/hiAgent/deleteConversation'
    }
  },

  data () {
    return {
      agentId: null,
      quickFuncList: [],
      showFuncModal: false, // 快速功能弹窗显隐
      // 消息相关
      messages: [],
      robotIcon,
      inputText: '',
      botName: '',
      botAvatar: robot,
      // 状态
      isFullScreen: false,
      showAttachments: false,
      showHistory: false,
      isInputFocused: false,
      isLoading: false,
      // 附件
      attachments: [],

      // 聊天历史
      chatHistory: [],
      conversationID: null,
      appConversationID: null,
      appConfig: null, // 应用配置
      userInfo: null, // 用户登录信息

      // 配置
      status: 'online', // online, offline, busy

      // 文本处理
      lastInputLength: 0,

      // 变量配置面板
      showVariableConfigPanel: false,
      variableConfigs: [], // 变量配置列表
      variableConfigValues: {}, // 用户选择的变量值
      variableConfigSelected: false, // 是否已选择变量配置

      // 初始化状态
      isInitializing: true // 是否正在初始化
    }
  },

  computed: {
    // 状态相关
    statusClass () {
      return {
        'status-online': this.status === 'online',
        'status-offline': this.status === 'offline',
        'status-busy': this.status === 'busy'
      }
    },

    statusText () {
      const statusMap = {
        online: '在线',
        offline: '离线',
        busy: '忙碌中'
      }
      return statusMap[this.status] || '未知'
    },

    // 发送按钮状态
    canSend () {
      return this.inputText.trim().length > 0 && this.isConnected && !this.isLoading
    },

    sendButtonIcon () {
      return this.canSend ? 'icon-send' : 'icon-send-disabled'
    },

    fullScreenIcon () {
      return this.isFullScreen ? 'icon-minimize' : 'icon-maximize'
    },

    // 输入框提示
    inputPlaceholder () {
      if (!this.isConnected) {
        return '连接中，请稍候...'
      }
      return this.isInputFocused ? '输入消息... (Shift+Enter换行)' : '输入消息...'
    },

    // 欢迎消息相关配置
    welcomeAvatar () {
      // if (this.appConfig) {
      //   return this.appConfig.image || this.appConfig.avatar || this.appConfig.logoUrl || this.botAvatar
      // }
      return this.botAvatar
    },

    welcomeTitle () {
      if (this.appConfig) {
        // 支持多种字段名：welcomeTitle, welcome, title, botName, name
        return this.appConfig.welcomeTitle ||
          this.appConfig.welcome ||
          (this.appConfig.botName || this.appConfig.name
            ? `我是${this.appConfig.botName || this.appConfig.name}`
            : `我是${this.botName}`)
      }
      return `我是${this.botName}`
    },

    welcomeDescription () {
      if (this.appConfig) {
        // 支持多种字段名：welcomeDescription, description, welcomeText, welcomeMsg
        return this.appConfig.welcomeDescription ||
          this.appConfig.description ||
          this.appConfig.welcomeText ||
          this.appConfig.welcomeMsg ||
          '有什么可以帮助您的吗？'
      }
      return '有什么可以帮助您的吗？'
    },

    welcomeQuickQuestions () {
      if (this.appConfig) {
        // 支持多种字段名：quickQuestions, questions, welcomeQuestions, suggestedQuestions
        const questions = this.appConfig.openQuery ||
          this.appConfig.questions ||
          this.appConfig.welcomeQuestions ||
          this.appConfig.suggestedQuestions

        if (questions && Array.isArray(questions) && questions.length > 0) {
          return questions
        }
      }
      // 如果配置中没有，使用 prop 中的默认值
      return this.quickQuestions
    },

    // 从登录信息获取用户名
    currentUserName () {
      if (this.userInfo) {
        return this.userInfo.userName ||
          this.userInfo.realName ||
          this.userInfo.nickName ||
          this.userInfo.name ||
          this.userName
      }
      return this.userName
    },

    // 从登录信息获取用户头像
    currentUserAvatar () {
      if (this.userInfo) {
        return this.userInfo.headImg ||
          defaultAvatar
      }
      return this.userAvatar || defaultAvatar
    },

    // 变量配置对话框标题
    variableConfigDialogTitle () {
      if (this.appConfig) {
        const appName = this.appConfig.botName || this.appConfig.name || ''
        if (appName) {
          return `${appName}·对话设置`
        }
      }
      return '对话设置'
    },

    // 是否可以提交变量配置
    canSubmitVariableConfig () {
      if (!this.variableConfigs || this.variableConfigs.length === 0) {
        return false
      }
      // 检查所有必填项是否都已填写
      return this.variableConfigs.every(config => {
        const key = config.key || config.name
        const value = this.variableConfigValues[key]
        if (config.required !== false) {
          return value !== undefined && value !== null && value !== ''
        }
        return true
      })
    }
  },
  created () {
    this.getQuickFuncList()
  },
  watch: {
    // 监听消息变化，自动滚动到底部
    messages: {
      handler () {
        this.$nextTick(() => {
          if (this.autoScroll) {
            this.scrollToBottom()
          }
        })
      },
      deep: true
    },

    // 监听输入框内容变化，自动调整高度
    inputText (newVal) {
      this.$nextTick(() => {
        this.adjustTextareaHeight()
      })
    },

    // 监听初始消息
    initialMessages: {
      immediate: true,
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          this.messages = [...newVal]
        }
      }
    },
    // 监听 agentId 变化，重新获取配置
    agentId: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal && newVal !== oldVal) { // 新增：避免重复初始化
          this.initAfterAgentIdReady()
        }
      }
    }
  },

  async mounted () {
    try {
      // 显示初始化加载动画
      this.isInitializing = true

      // 获取用户登录信息
      this.loadUserInfo()

      // 获取应用配置
      // await this.getAppConfig()

      // // 如果没有变量配置或已选择配置，初始化会话
      // if (this.variableConfigSelected || !this.showVariableConfigPanel) {
      //   await this.createConversation()
      // }
      // 初始化消息
      if (this.messages.length === 0 && this.showWelcome) {
        // this.addWelcomeMessage()
      }

      // 加载聊天历史
      this.loadChatHistoryFromStorage()

      // 添加键盘事件监听
      this.setupKeyboardShortcuts()

      // 初始化输入框高度
      this.adjustTextareaHeight()
    } finally {
      // 隐藏初始化加载动画
      this.isInitializing = false
    }
  },

  beforeDestroy () {
    // 清理事件监听
    this.removeKeyboardShortcuts()
  },

  methods: {
    async initAfterAgentIdReady () {
      if (!this.agentId) return // 双重校验，防止异常调用
      this.isInitializing = true // 开启初始化加载

      try {
        // 1. 调用 getAppConfig（需传入 agentId）
        await this.getAppConfig()
        // 2. 调用 createConversation（需传入 agentId）
        // await this.createConversation()
      } catch (err) {
        console.error('初始化失败:', err)
      } finally {
        this.isInitializing = false // 关闭初始化加载
      }
    },

    getQuickFuncList () {
      this.$request.post('/sso/hiAgent/config/findList', {}).then(res => {
        const { code, data } = res.data
        if (code === 'OK') {
          // 根据实际返回的数据结构调整，可能是 data.list 或 data.records 等
          this.quickFuncList = data.list || data.records || data || []
          // 如果列表不为空且还没有选中 agentId，默认选择第一个
          if (this.quickFuncList.length > 0 && !this.agentId) {
            const firstItem = this.quickFuncList[0]
            this.agentId = firstItem.id || firstItem.agentId || null
          }
        }
      }).catch(err => {
        console.error('获取快速功能列表失败:', err)
        this.quickFuncList = []
      })
    },
    // 获取功能项背景样式类
    getFuncBgClass (index) {
      const bgClasses = ['bg-DBEDFD', 'bg-D9F2EA', 'bg-E1E2F7', 'bg-FEE2DD']
      return bgClasses[index % bgClasses.length]
    },
    getDefaultIcon (index) {
      const icons = [
        require('../assets/images/common/richeng.png'),
        require('../assets/images/common/renwu.png'),
        require('../assets/images/common/zhishi.png'),
        require('../assets/images/common/shuju.png')
      ]
      return icons[index % icons.length]
    },
    handleFuncClick (item) {
      // 获取 item 的 id 作为 agentId
      const agentId = item.id || item.agentId
      if (agentId) {
        this.agentId = agentId
        // agentId 变化会触发 ChatPlugin 中的 watch，自动重新获取配置和创建会话
      }
      this.showFuncModal = false
    },
    // 加载用户登录信息
    loadUserInfo () {
      try {
        const userInfoStr = session.getItem('userInfo')
        if (userInfoStr) {
          this.userInfo = JSON.parse(userInfoStr)
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
        this.userInfo = null
      }
    },

    // 获取应用配置
    async getAppConfig () {
      try {
        const requestData = {}
        // 如果有 agentId，将其作为参数传递
        if (this.agentId) {
          requestData.agentId = this.agentId
        }
        const response = await this.$request.post(this.getAppConfigUrl, requestData)
        const { code, data } = response.data

        if (code === 'OK' && data) {
          // 保存应用配置
          this.appConfig = data

          // 如果配置中有机器人名称，更新 botName
          if (data.botName || data.name) {
            this.botName = data.botName || data.name
            this.$emit('update:botName', data.botName || data.name)
          }

          // 如果配置中有机器人头像，更新 botAvatar
          if (data.botAvatar || data.avatar || data.logoUrl) {
            this.botAvatar = data.image || data.avatar || data.logoUrl
            this.$emit('update:botAvatar', data.botAvatar || data.avatar || data.logoUrl)
          }

          // 检查是否有变量配置
          if (data.variableConfigs && Array.isArray(data.variableConfigs) && data.variableConfigs.length > 0) {
            // 如果有变量配置，显示配置选择面板
            this.variableConfigs = data.variableConfigs
            this.variableConfigSelected = false
            this.initVariableConfigValues()
            this.showVariableConfigPanel = true
            // 隐藏初始化加载动画
            this.isInitializing = false
          } else {
            // 如果没有变量配置，直接初始化会话
            this.showVariableConfigPanel = false
            this.variableConfigSelected = true
            await this.createConversation()
            // 隐藏初始化加载动画
            this.isInitializing = false
          }

          // 触发配置加载事件
          this.$emit('config-loaded', {
            config: data
          })
        }
      } catch (error) {
        console.error('获取应用配置失败:', error)
        // 配置获取失败不影响使用，可以继续使用组件
        this.$emit('config-error', error)
      }
    },

    // 创建会话
    async createConversation () {
      try {
        const requestData = {}
        // 如果有 agentId，将其作为参数传递
        if (this.agentId) {
          requestData.agentId = this.agentId
        }
        // 如果有变量配置值，将其添加到请求数据中
        if (this.variableConfigValues && Object.keys(this.variableConfigValues).length > 0) {
          requestData.inputs = this.variableConfigValues
        }
        const response = await this.$request.post(this.createConversationUrl, requestData)
        const { code, data } = response.data
        if (code === 'OK' && data) {
          // 保存会话ID
          if (data.conversation && data.conversation.conversationID) {
            this.conversationID = data.conversation.conversationID
          }
          // 保存 appConversationID
          if (data.conversation && data.conversation.appConversationID) {
            this.appConversationID = data.conversation.appConversationID
          }
          // 触发会话创建事件
          this.$emit('conversation-created', {
            conversationID: this.conversationID,
            appConversationID: this.appConversationID,
            data: data
          })
        }
      } catch (error) {
        console.error('创建会话失败:', error)
        // 创建会话失败不影响使用，可以继续使用组件
        this.$emit('conversation-error', error)
      }
    },

    // 消息处理
    async sendMessage () {
      if (!this.canSend) return

      const text = this.inputText.trim()
      if (!text) return

      // 添加用户消息
      const userMessage = this.addMessage({
        role: 'user',
        type: 'text',
        content: text,
        timestamp: new Date()
      })

      // 保存用户输入
      const messageText = text
      const messageAttachments = [...this.attachments]

      // 清空输入框
      this.inputText = ''

      // 如果有附件，添加附件信息
      if (this.attachments.length > 0) {
        this.addMessage({
          role: 'system',
          type: 'text',
          content: `已上传 ${this.attachments.length} 个文件`,
          timestamp: new Date()
        })
        this.attachments = []
      }

      // 触发发送事件
      this.$emit('send', {
        text: messageText,
        attachments: messageAttachments,
        timestamp: new Date()
      })

      // 调用接口发送消息
      await this.sendMessageToAPI(messageText, messageAttachments, userMessage)
    },

    addMessage (message) {
      const newMessage = {
        id: Date.now() + Math.random(),
        role: message.role || 'user',
        type: message.type || 'text',
        content: message.content || '',
        timestamp: message.timestamp || new Date(),
        language: message.language,
        liked: message.liked || false,
        likeCount: message.likeCount || 0,
        retryable: message.retryable || false
      }

      this.messages.push(newMessage)
      return newMessage
    },

    addWelcomeMessage () {
      this.addMessage({
        role: 'bot',
        type: 'text',
        content: `您好！我是${this.botName}，很高兴为您服务。有什么问题我可以帮您解答吗？`,
        timestamp: new Date()
      })
    },

    // 快速问题
    sendQuickQuestion (question) {
      this.inputText = question
      this.sendMessage()
    },

    // 调用 API 发送消息（流式响应）
    async sendMessageToAPI (text, attachments = [], userMessage) {
      this.isLoading = true
      this.$emit('typing-start')

      // 添加加载中的消息，保留加载状态
      const loadingMessage = this.addMessage({
        role: 'bot',
        type: 'loading',
        content: '',
        timestamp: new Date()
      })

      try {
        // 准备请求数据
        const requestData = {
          query: text,
          conversationID: this.conversationID,
          appConversationID: this.appConversationID
        }
        // 如果有 agentId，将其添加到请求数据中
        if (this.agentId) {
          requestData.agentId = this.agentId
        }
        if (attachments.length > 0) {
          requestData.queryExtends = {
            files: attachments.map(item => ({
              name: item.name,
              size: item.size,
              url: item.url,
              path: item.url
            }))
          }
        }

        // 获取 token
        const token = session.parseJSON() || cookie.getCookie('token') || ''

        // 获取 baseURL
        const baseURL = this.$baseUrl || window.location.origin + '/'
        const url = baseURL.replace(/\/$/, '') + this.apiUrl

        // 使用 fetch 处理流式响应
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authorization': token || ''
          },
          body: JSON.stringify(requestData)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // 检查响应类型是否为流式
        const contentType = response.headers.get('content-type') || ''
        if (contentType.includes('text/event-stream') || contentType.includes('text/plain')) {
          // 处理流式响应
          await this.handleStreamResponse(response, loadingMessage)
        } else {
          // 如果不是流式响应，按普通响应处理
          const data = await response.json()
          this.handleAPIResponse({ data: data }, loadingMessage)
        }
      } catch (error) {
        console.error('发送消息失败:', error)
        this.handleAPIError(error, loadingMessage, text, attachments)
      } finally {
        this.isLoading = false
        this.$emit('typing-end')
      }
    },

    // 处理流式响应
    async handleStreamResponse (response, loadingMessage) {
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''
      let isFirstChunk = true

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }

          // 解码数据块
          buffer += decoder.decode(value, { stream: true })

          // 处理 SSE 格式的数据
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // 保留最后一个不完整的行
          for (const line of lines) {
            if (line.trim() === '') continue
            // 处理 SSE 格式: data: {...}
            if (line.startsWith('data:')) {
              try {
                const dataStr = line.substring(5) // 移除 'data: ' 前缀

                // 处理 [DONE] 标记
                if (dataStr.trim() === '[DONE]') {
                  break
                }

                const data = JSON.parse(dataStr)

                // 处理流式数据
                if (data.content || data.text || data.answer || data.delta) {
                  const chunk = data.content || data.text || data.answer || data.delta || ''

                  if (chunk) {
                    fullContent += chunk

                    // 如果是第一个数据块，将 loading 消息转换为文本消息
                    if (isFirstChunk) {
                      loadingMessage.type = 'text'
                      loadingMessage.content = fullContent
                      isFirstChunk = false
                    } else {
                      // 实时更新消息内容
                      loadingMessage.content = fullContent
                    }

                    // 触发流式更新事件
                    this.$emit('stream-chunk', {
                      chunk: chunk,
                      fullContent: fullContent,
                      message: loadingMessage
                    })
                  }
                }

                // 如果流结束，处理最终数据
                if (data.done || data.finished || data.stop) {
                  // 确保最终内容已更新
                  if (fullContent) {
                    loadingMessage.content = fullContent
                  }

                  // 更新会话ID
                  if (data.conversationID) {
                    this.conversationID = data.conversationID
                  }
                  if (data.appConversationID) {
                    this.appConversationID = data.appConversationID
                  }

                  // 触发接收事件
                  this.$emit('message-received', {
                    message: loadingMessage,
                    data: data
                  })

                  // 保存聊天历史
                  this.saveChatHistory()

                  break
                }
              } catch (e) {
                console.warn('解析流式数据失败:', e, line)
              }
            }
            // 处理纯文本格式（非 SSE）
            // else if (line.trim() && !line.startsWith(':')) {
            //   try {
            //     const data = JSON.parse(line)
            //     if (data.content || data.text || data.answer) {
            //       const chunk = data.content || data.text || data.answer
            //       fullContent += chunk

            //       if (isFirstChunk) {
            //         loadingMessage.type = 'text'
            //         loadingMessage.content = fullContent
            //         isFirstChunk = false
            //       } else {
            //         loadingMessage.content = fullContent
            //       }
            //     }
            //   } catch (e) {
            //     // 如果不是 JSON，可能是纯文本内容
            //     if (isFirstChunk) {
            //       loadingMessage.type = 'text'
            //       loadingMessage.content = line
            //       isFirstChunk = false
            //     } else {
            //       loadingMessage.content += line
            //     }
            //     fullContent += line
            //   }
            // }
          }
        }

        // 确保最终内容已更新
        if (fullContent && loadingMessage.type === 'loading') {
          loadingMessage.type = 'text'
          loadingMessage.content = fullContent
        }

        // 如果流结束时没有触发完成事件，手动触发
        if (loadingMessage.type === 'text' && loadingMessage.content) {
          this.$emit('message-received', {
            message: loadingMessage,
            data: { content: fullContent }
          })
          this.saveChatHistory()
        }
      } catch (error) {
        console.error('读取流式响应失败:', error)
        throw error
      } finally {
        reader.releaseLock()
      }
    },

    // 处理 API 响应
    handleAPIResponse (response, loadingMessage) {
      // 移除加载中的消息
      const loadingIndex = this.messages.findIndex(m => m.id === loadingMessage.id)
      if (loadingIndex > -1) {
        this.messages.splice(loadingIndex, 1)
      }

      const { code, data } = response.data

      if (code === 'OK' && data) {
        // 添加机器人回复消息
        // 支持多种响应字段：answer, content, message, reply
        const botMessage = this.addMessage({
          role: 'bot',
          type: 'text',
          content: data.answer || data.content || data.message || data.reply || '收到回复',
          timestamp: new Date(data.timestamp || new Date())
        })

        // 如果有 conversationID，更新当前聊天ID
        if (data.conversationID) {
          this.conversationID = data.conversationID
        } else if (data.chatId) {
          this.conversationID = data.chatId
        }

        // 如果有 appConversationID，更新它
        if (data.appConversationID) {
          this.appConversationID = data.appConversationID
        }

        // 如果有代码块，特殊处理
        if (data.code) {
          botMessage.type = 'code'
          botMessage.content = data.code
          botMessage.language = data.language || 'text'
        }

        // 触发接收事件
        this.$emit('message-received', {
          message: botMessage,
          data: data
        })

        // 保存聊天历史
        this.saveChatHistory()
      } else {
        // API 返回错误
        this.addMessage({
          role: 'bot',
          type: 'error',
          content: response.data.message || '服务器返回错误',
          timestamp: new Date(),
          retryable: true
        })
      }
    },

    // 处理 API 错误
    handleAPIError (error, loadingMessage, text, attachments) {
      // 移除加载中的消息
      const loadingIndex = this.messages.findIndex(m => m.id === loadingMessage.id)
      if (loadingIndex > -1) {
        this.messages.splice(loadingIndex, 1)
      }

      // 添加错误消息
      let errorContent = '网络错误，请稍后重试'
      if (error.message) {
        errorContent = error.message
      } else if (error.response && error.response.data && error.response.data.message) {
        errorContent = error.response.data.message
      }

      const errorMessage = this.addMessage({
        role: 'bot',
        type: 'error',
        content: errorContent,
        timestamp: new Date(),
        retryable: true
      })

      // 保存错误信息，用于重试
      errorMessage.retryData = {
        text,
        attachments,
        userMessage: this.messages[this.messages.length - 2] // 保存用户消息引用
      }

      // 触发错误事件
      this.$emit('message-error', {
        error: error,
        message: errorMessage
      })
    },

    // 重试消息
    async retryMessage (errorMessage) {
      if (!errorMessage.retryData) {
        this.$emit('retry', errorMessage)
        return
      }

      // 移除错误消息
      const errorIndex = this.messages.findIndex(m => m.id === errorMessage.id)
      if (errorIndex > -1) {
        this.messages.splice(errorIndex, 1)
      }

      // 重新发送消息
      await this.sendMessageToAPI(
        errorMessage.retryData.text,
        errorMessage.retryData.attachments || [],
        errorMessage.retryData.userMessage
      )
    },

    // 清空聊天
    clearChat () {
      if (this.messages.length > 0) {
        if (confirm('确定要清空聊天记录吗？')) {
          this.messages = []
          if (this.showWelcome) {
            this.addWelcomeMessage()
          }
          this.$emit('clear')
        }
      }
    },

    // 消息渲染
    renderMessage (content) {
      // 简单的 Markdown 渲染
      let rendered = content
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>')

      // 链接检测
      rendered = rendered.replace(
        /(https?:\/\/[^\s]+)/g,
        '<a href="$1" target="_blank" class="message-link">$1</a>'
      )

      return rendered
    },

    // 复制功能
    copyText (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$emit('copy-success', '文本已复制到剪贴板')
      }).catch(err => {
        console.error('复制失败:', err)
        this.$emit('copy-error', err)
      })
    },

    copyCode (code) {
      this.copyText(code)
    },

    // 重新生成消息
    async regenerateMessage (message) {
      // 找到用户消息
      const messageIndex = this.messages.findIndex(m => m.id === message.id)
      if (messageIndex === -1) return

      // 找到对应的用户消息（通常是前一条）
      let userMessageIndex = messageIndex - 1
      while (userMessageIndex >= 0 && this.messages[userMessageIndex].role !== 'user') {
        userMessageIndex--
      }

      if (userMessageIndex >= 0) {
        const userMessage = this.messages[userMessageIndex]
        // 移除当前机器人消息
        this.messages.splice(messageIndex, 1)
        // 重新发送
        await this.sendMessageToAPI(userMessage.content, [], userMessage)
      } else {
        this.$emit('regenerate', message)
      }
    },

    toggleMessageLike (message) {
      message.liked = !message.liked
      message.likeCount = message.liked
        ? (message.likeCount || 0) + 1
        : Math.max(0, (message.likeCount || 1) - 1)

      this.$emit('message-like', {
        messageId: message.id,
        liked: message.liked,
        likeCount: message.likeCount
      })
    },

    // 附件处理
    async handleFileUpload (event) {
      const files = Array.from(event.target.files)
      event.target.value = '' // 重置input

      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { // 10MB限制
          this.$emit('file-error', '文件大小不能超过10MB')
          continue
        }

        // 创建附件对象，包含上传状态
        const attachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          uploading: true,
          progress: 0,
          url: null,
          error: null
        }

        // 先添加到列表，显示上传中状态
        this.attachments.push(attachment)

        try {
          // 调用上传接口
          await this.uploadFile(file, attachment)
        } catch (error) {
          console.error('文件上传失败:', error)
          attachment.uploading = false
          attachment.error = error.message || '上传失败'
          this.$emit('file-error', `${file.name} 上传失败: ${attachment.error}`)
        }
      }

      this.$emit('file-upload', this.attachments)
    },

    // 上传文件
    async uploadFile (file, attachment) {
      try {
        // 创建 FormData
        const formData = new FormData()
        formData.append('file', file)
        formData.append('fileName', file.name)
        formData.append('fileSize', file.size)

        // 获取 token
        const token = session.parseJSON() || cookie.getCookie('token') || ''
        // 获取 baseURL
        const baseURL = this.$baseUrl || window.location.origin + '/'
        const url = baseURL.replace(/\/$/, '') + '/sso/hiAgent/upload/createMultipartUpload'

        // 使用 axios 直接调用，支持上传进度
        // 注意：不设置 Content-Type，让浏览器自动设置（包含 boundary）
        const response = await axios({
          method: 'post',
          url: url,
          data: formData,
          headers: {
            'authorization': token
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              attachment.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            }
          }
        })

        const { code, data } = response.data
        if (code === 'OK') {
          // 上传成功，保存文件信息
          attachment.uploading = false
          attachment.progress = 100
          attachment.url = data.url || data.path || data.fileUrl || data.filePath
          attachment.uploadId = data.uploadId
        } else {
          throw new Error(response.data.message || '上传失败')
        }
      } catch (error) {
        attachment.uploading = false
        attachment.error = error.response?.data?.message || error.message || '上传失败'
        throw error
      }
    },

    removeAttachment (index) {
      const removed = this.attachments.splice(index, 1)[0]
      this.$emit('file-remove', removed)
    },

    // 历史记录
    loadChatHistoryFromStorage () {
      try {
        const history = localStorage.getItem('ai-chat-history')
        if (history) {
          this.chatHistory = JSON.parse(history)
        }
      } catch (error) {
        console.error('加载聊天历史失败:', error)
      }
    },

    saveChatHistory () {
      if (this.messages.length === 0) return

      const historyItem = {
        id: this.conversationID || Date.now(),
        title: this.generateChatTitle(),
        messages: [...this.messages],
        createdAt: new Date(),
        updatedAt: new Date()
      }

      // 更新或添加历史记录
      const index = this.chatHistory.findIndex(h => h.id === historyItem.id)
      if (index > -1) {
        this.chatHistory[index] = historyItem
      } else {
        this.chatHistory.unshift(historyItem)
      }

      // 限制历史记录数量
      if (this.chatHistory.length > 50) {
        this.chatHistory = this.chatHistory.slice(0, 50)
      }

      // 保存到本地存储
      localStorage.setItem('ai-chat-history', JSON.stringify(this.chatHistory))
    },

    generateChatTitle () {
      if (this.messages.length === 0) return '新对话'

      const userMessages = this.messages.filter(m => m.role === 'user')
      if (userMessages.length === 0) return 'AI对话'

      const firstMessage = userMessages[0].content
      return firstMessage.length > 20
        ? firstMessage.substring(0, 20) + '...'
        : firstMessage
    },

    async loadChatHistory (history) {
      // 设置当前会话ID
      if (history.conversationID) {
        this.conversationID = history.conversationID
      } else if (history.id) {
        this.conversationID = history.id
      }

      // 设置 appConversationID
      if (history.appConversationID) {
        this.appConversationID = history.appConversationID
      }

      // 如果有消息列表，直接加载
      if (history.messages && history.messages.length > 0) {
        this.messages = history.messages
        this.showHistory = false
        this.$emit('history-loaded', history)
      } else {
        // 如果没有消息列表，调用接口获取历史消息
        try {
          await this.getConversationMessages(history.appConversationID || history.appConversationId)
          this.showHistory = false
          this.$emit('history-loaded', history)
        } catch (error) {
          console.error('加载历史消息失败:', error)
          this.messages = []
          this.showHistory = false
        }
      }
    },

    // 获取历史会话消息
    async getConversationMessages (appConversationID) {
      if (!appConversationID) {
        console.warn('appConversationID 为空，无法获取历史消息')
        return
      }

      try {
        // const url = `${this.getConversationMessagesUrl}?appConversationID=${appConversationID}`
        const response = await this.$request.post(this.getConversationMessagesUrl, {
          appConversationID: appConversationID,
          agentId: this.agentId
        })
        const { code, data } = response.data

        if (code === 'OK' && data) {
          // 格式化消息列表
          let messages = []

          // 如果返回的是数组
          if (Array.isArray(data)) {
            messages = data.map(item => this.formatMessageItem(item))
          }
          // 如果返回的是对象，包含消息列表字段
          else if (data.messages && Array.isArray(data.messages)) {
            data.messages.map(item => {
              {
                messages.push(this.formatMessageItem(item))
                messages.push(this.formatMessageItem(item.answerInfo))
              }
            })
          } else if (data.messageList && Array.isArray(data.messageList)) {
            messages = data.messageList.map(item => this.formatMessageItem(item))
          }
          // 如果返回的是单个消息对象
          else if (data.content || data.query || data.answer) {
            messages = [this.formatMessageItem(data)]
          }

          // 按时间排序消息
          messages.sort((a, b) => {
            const timeA = new Date(a.timestamp).getTime()
            const timeB = new Date(b.timestamp).getTime()
            return timeA - timeB
          })

          // 更新消息列表
          this.messages = messages

          // 触发消息加载事件
          this.$emit('messages-loaded', {
            messages: messages,
            appConversationID: appConversationID
          })
        }
      } catch (error) {
        console.error('获取历史消息失败:', error)
        this.$emit('messages-error', error)
        throw error
      }
    },

    // 格式化消息项数据
    formatMessageItem (item) {
      // 判断消息角色
      let role = 'bot'
      if (item.role === 'user' || item.role === 'USER' || item.query || item.userMessage) {
        role = 'user'
      } else if (item.role === 'assistant' || item.role === 'ASSISTANT' || item.answer || item.content) {
        role = 'bot'
      }

      // 获取消息内容
      let content = ''
      if (role === 'user') {
        content = item.query || item.userMessage || item.content || item.text || ''
      } else {
        content = item.answer || item.content || item.text || item.message || ''
      }

      return {
        id: item.id || item.messageId || Date.now() + Math.random(),
        role: role,
        type: item.type || 'text',
        content: content,
        timestamp: item.timestamp || item.createTime || item.createdAt || new Date(),
        language: item.language,
        liked: item.liked || false,
        likeCount: item.likeCount || 0
      }
    },

    deleteChatHistory (historyId, appConversationID) {
      if (confirm('确定要删除这条聊天记录吗？')) {
        this.$request.post(this.deleteConversationUrl, {
          appConversationID: appConversationID,
          agentId: this.agentId
        }).then(res => {
          console.log(res)
          if (res.data.code === 'OK') {
            this.chatHistory = this.chatHistory.filter(h => h.id !== historyId)
            localStorage.setItem('ai-chat-history', JSON.stringify(this.chatHistory))
            if (this.conversationID === historyId) {
              console.log(this.conversationID)
              this.messages = []
              this.conversationID = null
              if (this.showWelcome) {
                this.createConversation()
              }
            }

            this.$emit('history-deleted', historyId)
          }
        })
      }
    },

    // UI控制
    toggleFullScreen () {
      this.isFullScreen = !this.isFullScreen
      this.$emit('fullscreen-change', this.isFullScreen)
    },

    toggleAttachments () {
      this.showAttachments = !this.showAttachments
      if (this.showAttachments) {
        this.showHistory = false
      }
    },

    async toggleHistory () {
      this.showHistory = !this.showHistory
      if (this.showHistory) {
        this.showAttachments = false
        // 打开历史记录侧边栏时，获取历史会话列表
        await this.getConversationList()
      }
    },

    // 获取历史会话列表
    async getConversationList () {
      try {
        const response = await this.$request.post(this.getConversationListUrl, { agentId: this.agentId })
        const { code, data } = response.data

        if (code === 'OK' && data) {
          // 转换数据格式
          let conversationList = []

          // 如果返回的是数组
          if (Array.isArray(data)) {
            conversationList = data.map(item => this.formatConversationItem(item))
          }
          // 如果返回的是对象，包含列表字段
          else if (data.conversationList && Array.isArray(data.conversationList)) {
            conversationList = data.conversationList.map(item => this.formatConversationItem(item))
          }
          // 如果返回的是对象，直接转换
          else if (data.conversationID || data.id) {
            conversationList = [this.formatConversationItem(data)]
          }

          // 更新历史记录列表
          this.chatHistory = conversationList

          // 触发历史记录加载事件
          this.$emit('conversation-list-loaded', {
            list: conversationList,
            data: data
          })
        }
      } catch (error) {
        console.error('获取历史会话列表失败:', error)
        // 获取失败时，可以继续使用本地存储的历史记录
        this.$emit('conversation-list-error', error)
      }
    },

    // 格式化会话项数据
    formatConversationItem (item) {
      return {
        id: item.conversationID || item.id || item.conversationId,
        title: item.title || item.conversationName || item.query || '未命名对话',
        createdAt: item.createTime || item.createdAt || item.createAt || new Date(),
        updatedAt: item.updateTime || item.updatedAt || item.updateAt || new Date(),
        messages: item.messages || [],
        conversationID: item.conversationID || item.id || item.conversationId,
        appConversationID: item.appConversationID || item.appConversationId
      }
    },

    insertEmoji () {
      // 实际实现中可以使用emoji选择器库
      this.inputText += '😊'
      this.$refs.inputTextarea.focus()
    },

    // 输入框处理
    handleShiftEnter () {
      this.inputText += '\n'
    },

    handleInput () {
      const currentLength = this.inputText.length
      if (currentLength > this.maxLength) {
        this.inputText = this.inputText.substring(0, this.maxLength)
      }
      this.lastInputLength = currentLength

      this.$emit('input-change', this.inputText)
    },

    handleFocus () {
      this.isInputFocused = true
    },

    handleBlur () {
      this.isInputFocused = false
    },

    adjustTextareaHeight () {
      const textarea = this.$refs.inputTextarea
      if (!textarea) return

      textarea.style.height = 'auto'
      const newHeight = Math.min(textarea.scrollHeight, 150) // 最大150px
      textarea.style.height = newHeight + 'px'
    },

    // 滚动控制
    scrollToBottom () {
      const container = this.$refs.chatBody
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // 键盘快捷键
    setupKeyboardShortcuts () {
      this.onKeydown = (event) => {
        // Ctrl + Enter 发送
        if (event.ctrlKey && event.key === 'Enter') {
          this.sendMessage()
        }

        // Esc 关闭附件/历史面板
        if (event.key === 'Escape') {
          if (this.showAttachments) this.showAttachments = false
          if (this.showHistory) this.showHistory = false
        }
      }

      window.addEventListener('keydown', this.onKeydown)
    },

    removeKeyboardShortcuts () {
      if (this.onKeydown) {
        window.removeEventListener('keydown', this.onKeydown)
      }
    },

    // 初始化变量配置值
    initVariableConfigValues () {
      this.variableConfigValues = {}
      if (this.variableConfigs && this.variableConfigs.length > 0) {
        this.variableConfigs.forEach(config => {
          const key = config.key || config.name
          this.variableConfigValues[key] = config.defaultValue || undefined
        })
      }
    },

    // 显示配置项提示信息
    showConfigTooltip (config) {
      const message = config.description || config.tooltip
      if (message) {
        this.$Message.info({
          content: message,
          duration: 3
        })
      }
    },

    // 处理变量配置面板提交
    async handleVariableConfigSubmit () {
      if (!this.canSubmitVariableConfig) {
        this.$Message.warning('请填写所有必填项')
        return
      }
      try {
        // 显示初始化加载动画
        this.isInitializing = true
        // 标记已选择配置
        this.variableConfigSelected = true
        // 隐藏配置面板
        this.showVariableConfigPanel = false
        // 初始化会话
        await this.createConversation()
      } finally {
        // 隐藏初始化加载动画
        this.isInitializing = false
      }
    },

    // 工具函数
    formatTime (timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDate (timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date

      if (diff < 24 * 60 * 60 * 1000) {
        return date.toLocaleTimeString('zh-CN', {
          hour: '2-digit',
          minute: '2-digit'
        })
      } else if (diff < 7 * 24 * 60 * 60 * 1000) {
        const days = Math.floor(diff / (24 * 60 * 60 * 1000))
        return `${days}天前`
      } else {
        return date.toLocaleDateString('zh-CN')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.header-title {
  font-size: 80px;
  font-weight: 600;
  color: #333;
}

.header-btn {
  padding: 0.06rem 0.12rem;
  background-color: #007aff;
  color: #fff;
  border: none;
  border-radius: 30px;
  font-size: 80px;
  cursor: pointer;
}

/* 弹窗遮罩 */
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

/* 弹窗内容 */
.modal-content {
  background-color: #fff;
  border-radius: 50px;
  padding: 30px;
  width: 80%; /* 略大于快速功能卡片宽度 */
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 100px;
  padding-bottom: 50px;
  border-bottom: 5px solid #eee;
}

.modal-header span {
  font-size: 80px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  border: none;
  background: transparent;
  font-size: 100px;
  cursor: pointer;
  color: #999;
}

/* ====================== 复用assistant.vue的样式 ====================== */
.card-header {
  margin-bottom: 30px;
  font-size: 60px;
  font-weight: 500;
  color: #333;
}

.func-item {
  border-radius: 40px;
  display: flex;
  align-items: center;
  padding: 50px 50px;
  margin-bottom: 50px;
  cursor: pointer;
}

.func-item img {
  width: 300px;
  height: 300px;
  margin-right: 40px;
}

.func-item div {
  font-family: PingFangSC, PingFang SC;
  font-weight: 500;
  font-size: 70px;
  color: #333333;
  text-align: left;
  font-style: normal;
}

.func-item span {
  color: #666666;
  font-size: 70px;
  font-weight: 400;
}

/* 背景样式类 */
.bg-DBEDFD {
  background-color: #DBEDFD;
}

.bg-D9F2EA {
  background-color: #D9F2EA;
}

.bg-E1E2F7 {
  background-color: #E1E2F7;
}

.bg-FEE2DD {
  background-color: #FEE2DD;
}

.ai-chat-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  // max-height: 80vh;
  background: #FFFFFF;
  border-radius: 80px;
  overflow: hidden;
  box-shadow: 0px 20px 30px 0px rgba(221, 221, 221, 0.59);
  transition: all 0.3s ease;

  &.full-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    z-index: 1000;
  }
}

/* 头部样式 */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 80px 120px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(50px);
  border-bottom: 5px solid rgba(255, 255, 255, 0.2);
  .header-left {
    display: flex;
    align-items: center;
    gap: 60px;

    .avatar {
      width: 200px;
      height: 200px;
      border-radius: 50%;
      overflow: hidden;
      border: 10px solid white;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .header-info {
      .bot-name {
        margin: 0;
        font-size: 90px;
        font-weight: 600;
        color: white;
      }

      .status {
        font-size: 60px;
        padding: 10px 40px;
        border-radius: 50px;
        background: rgba(255, 255, 255, 0.2);

        &.status-online {
          color: #4ade80;
        }

        &.status-offline {
          color: #9ca3af;
        }

        &.status-busy {
          color: #fbbf24;
        }
      }
    }
  }

  .header-right {
    display: flex;
    gap: 40px;

    .header-btn {
      width: 180px;
      height: 180px;
      border-radius: 50%;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: translateY(-5px);
      }

      &.close-btn {
        background: rgba(239, 68, 68, 0.2);

        &:hover {
          background: rgba(239, 68, 68, 0.4);
        }
      }
    }
  }
}

/* 聊天主体样式 */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 120px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%);

  &::-webkit-scrollbar {
    width: 30px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 15px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

/* 欢迎消息 */
.welcome-message {
  position: relative;

  .welcome-content {
    text-align: right;
    max-width: 2000px;
    margin-left: 230px;
    flex-direction: column;
    display: flex;
    align-items: end;
    justify-content: flex-end;

    .welcome-avatar {
      z-index: 1;
      padding-right: 120px;
      position: absolute;
      top: 0;
      left: 0;
      width: 640px;
      height: 690px;
      background-color: #FFF;
      border-radius: 0 0 50% 0;
      padding: 50px;
      margin-bottom: 100px;
    }

    .welcome-content-inner {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      font-family: PingFangSC, PingFang SC;
      font-weight: 400;
      font-size: 80px;
      color: #333333;
      line-height: 110px;
      text-align: left;
      font-style: normal;
      width: 70%;

      h3 {
        color: #333;
        font-size: 120px;
        margin-bottom: 40px;
      }
    }

    .quick-questions {
      display: flex;
      flex-wrap: wrap;
      gap: 40px;
      justify-content: center;
      background: #EFF5FF;
      border-radius: 40px;
      width: 100%;
      padding: 120px;
      position: relative;

      .quick-questions-inner {
        width: 100%;
        text-align: right;
        display: flex;
        justify-content: flex-end;
        align-items: center;

        p {
          color: #333;
          margin-bottom: 120px;
          text-align: left;
          width: 70%;
        }
      }

      .quick-question-btn {
        display: flex;
        width: 1500px;
        padding: 40px 80px;
        color: #333;
        font-size: 70px;
        cursor: pointer;
        transition: all 0.2s;
        background: #FFFFFF;
        border-radius: 40px;
        backdrop-filter: blur(50px);

        &:hover {
          background: #5D3B05;
          color: #FFF;
          transform: translateY(-5px);
        }
      }
    }
  }
}

/* 消息列表 */
.messages-container {
  display: flex;
  flex-direction: column;
  gap: 100px;
}

/* 消息项 */
.message-item {
  display: flex;
  gap: 60px;
  animation: fadeIn 0.3s ease;

  &.user-message {
    flex-direction: row-reverse;

    .message-content {
      align-items: flex-end;
    }

    .message-body {
      background: #5D3B05;
      border-radius: 40px;
      color: white;
      font-size: 70px;
    }
  }

  &.bot-message {
    .message-body {
      background: #EFF5FF;
      backdrop-filter: blur(50px);
      color: #333;
      font-size: 70px;
    }
  }

  &.system-message {
    justify-content: start;

    .message-content {
      max-width: 70%;
    }

    .message-body {
      background: #EFF5FF;
      color: #333;
      font-size: 60px;
      padding: 40px 80px;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(50px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-avatar {
  flex-shrink: 0;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  overflow: hidden;
  border: 10px solid rgba(255, 255, 255, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.message-content {
  flex: 1;
  max-width: 70%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 40px;
  margin-bottom: 20px;

  .sender-name {
    font-size: 60px;
    font-weight: 500;
    color: #333;
  }

  .message-time {
    font-size: 55px;
    color: #333;
  }
}

.message-body {
  border-radius: 40px;
  padding: 60px 80px;
  word-break: break-word;
  line-height: 1.5;

  .message-link {
    color: #93c5fd;
    text-decoration: underline;

    &:hover {
      color: #60a5fa;
    }
  }

  code {
    background: rgba(0, 0, 0, 0.2);
    padding: 10px 30px;
    border-radius: 20px;
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 70px;
  }
}

/* 代码消息 */
.code-message {
  background: rgba(0, 0, 0, 0.3) !important;
  border-radius: 40px;
  overflow: hidden;

  .code-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 40px 80px;
    background: rgba(0, 0, 0, 0.4);
    border-bottom: 5px solid rgba(255, 255, 255, 0.1);

    .code-language {
      font-size: 60px;
      color: rgba(255, 255, 255, 0.7);
      font-family: 'Monaco', 'Consolas', monospace;
    }

    .copy-btn {
      background: rgba(255, 255, 255, 0.1);
      border: none;
      color: white;
      padding: 20px 60px;
      border-radius: 20px;
      font-size: 60px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }

  pre {
    margin: 0;
    padding: 80px;
    overflow-x: auto;

    code {
      background: none;
      padding: 0;
      color: #f8f8f2;
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 65px;
      line-height: 1.4;
    }
  }
}

/* 加载消息 */
.loading-message {
  padding: 60px 80px;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 20px;

  span {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    animation: typing 1.4s ease-in-out infinite;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing {

  0%,
  60%,
  100% {
    transform: translateY(0);
  }

  30% {
    transform: translateY(-20px);
  }
}

/* 错误消息 */
.error-message {
  background: rgba(239, 68, 68, 0.1) !important;
  border: 5px solid rgba(239, 68, 68, 0.3);

  .error-content {
    display: flex;
    align-items: center;
    gap: 40px;

    .icon-error {
      color: #ef4444;
    }

    .retry-btn {
      margin-left: auto;
      background: rgba(239, 68, 68, 0.2);
      border: none;
      color: white;
      padding: 20px 60px;
      border-radius: 20px;
      font-size: 60px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(239, 68, 68, 0.4);
      }
    }
  }
}

/* 消息操作 */
.message-actions {
  display: flex;
  gap: 40px;
  margin-top: 40px;
  opacity: 0;
  transition: opacity 0.2s;

  .message-item:hover & {
    opacity: 1;
  }

  .action-btn {
    display: flex;
    align-items: center;
    background: #EFF5FF;
    border: 5px solid rgba(255, 255, 255, 0.2);
    border-radius: 60px;
    color: #333;
    padding: 20px 60px;
    font-size: 60px;
    transition: all 0.2s;

    &:hover {
      // background: rgba(255, 255, 255, 0.2);
    }

    .icon-like.liked {
      color: #ef4444;
    }
  }
}

/* 聊天底部 */
.chat-footer {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(50px);
  border-top: 5px solid rgba(255, 255, 255, 0.1);
  padding: 80px 120px;
}

.toolbar {
  display: flex;
  gap: 60px;
  margin-bottom: 60px;

  .toolbar-btn {
    width: 180px;
    height: 180px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      transform: translateY(-5px);
    }
  }
}

/* 附件面板 */
.attachments-panel {
  background: #F0F5FF;
  border-radius: 60px;
  margin-bottom: 60px;
  padding: 80px;

  .attachments-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 60px;

    h4 {
      margin: 0;
      color: #000;
      font-size: 70px;
    }

    .close-attachments {
      background: none;
      border: none;
      color: #333;
      cursor: pointer;

      &:hover {
        color: #000;
      }
    }
  }

  .attachments-content {
    .file-upload-btn {
      display: inline-flex;
      align-items: center;
      gap: 40px;
      background: rgba(255, 255, 255, 0.9);
      border: 10px dashed rgba(255, 255, 255, 0.3);
      border-radius: 40px;
      color: #333;
      padding: 60px 100px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.5);
      }

      input {
        display: none;
      }
    }

    .attachments-list {
      margin-top: 60px;
      display: flex;
      flex-wrap: wrap;
      gap: 40px;

      .attachment-item {
        display: flex;
        align-items: center;
        gap: 40px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 30px;
        padding: 40px 60px;
        font-size: 60px;

        .file-name {
          color: #333;
          max-width: 750px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .upload-progress {
          display: flex;
          align-items: center;
          gap: 40px;
          min-width: 500px;

          .progress-bar {
            flex: 1;
            height: 20px;
            background: #e0e0e0;
            border-radius: 10px;
            overflow: hidden;

            .progress-fill {
              height: 100%;
              background: #4CA4F6;
              transition: width 0.3s ease;
            }
          }

          .progress-text {
            color: #666;
            font-size: 55px;
            min-width: 175px;
          }
        }

        .upload-success {
          color: #42C094;
          font-size: 70px;
        }

        .upload-error {
          color: #ff4d4f;
          font-size: 55px;
          max-width: 500px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .remove-attachment {
          background: none;
          border: none;
          color: #333;
          cursor: pointer;
          padding: 0;
          width: 80px;
          height: 80px;

          &:hover {
            color: #333;
          }
        }
      }
    }
  }
}

/* 输入容器 */
.input-container {
  .input-wrapper {
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 60px;

    .message-input {
      flex: 1;
      width: 2585px;
      min-height: 220px;
      max-height: 750px;
      padding: 60px 80px;
      background: rgba(255, 255, 255, 0.1);
      border: 10px solid #5D3B05;
      border-radius: 40px;
      color: #333;
      font-size: 70px;
      line-height: 1.5;
      resize: none;
      transition: all 0.2s;

      &::-webkit-scrollbar {
        width: 0;
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.6);
      }

      &:focus {
        // outline: none;
        // border-color: rgba(255, 255, 255, 0.4);
        // background: rgba(255, 255, 255, 0.15);
      }
    }

    .send-btn {
      position: absolute;
      right: 80px;
      top: 40px;
      width: 340px;
      height: 140px;
      border-radius: 60px;
      font-size: 70px;
      border: none;
      background: rgba(93, 59, 5, 0.6);
      color: #FFF;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;

      &:hover:not(.disabled) {
        transform: translateY(-10px);
        box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);
      }

      &.disabled {
        background: #999;
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }

  .footer-status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 40px;
    font-size: 60px;

    .char-count {
      color: rgba(255, 255, 255, 0.6);
    }

    .connection-status {
      display: flex;
      align-items: center;
      gap: 20px;

      &.connected {
        color: #4ade80;
      }

      &.disconnected {
        color: #fbbf24;
      }
    }
  }
}

/* 历史记录侧边栏 */
.history-sidebar {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 1600px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(100px);
  border-left: 5px solid rgba(255, 255, 255, 0.1);
  z-index: 10;

  .sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 120px;
    border-bottom: 5px solid rgba(255, 255, 255, 0.1);

    h3 {
      margin: 0;
      color: white;
      font-size: 90px;
    }

    .close-sidebar {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;

      &:hover {
        color: white;
      }
    }
  }

  .sidebar-content {
    padding: 80px;
    overflow-y: auto;
    height: calc(100%);

    .history-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 60px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 40px;
      margin-bottom: 40px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      &.active {
        background: rgba(102, 126, 234, 0.2);
        border: 5px solid rgba(102, 126, 234, 0.5);
      }

      .history-preview {
        flex: 1;
        overflow: hidden;

        .history-title {
          color: white;
          font-size: 70px;
          margin-bottom: 20px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .history-time {
          color: rgba(255, 255, 255, 0.6);
          font-size: 60px;
        }
      }

      .delete-history {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.6);
        cursor: pointer;
        padding: 20px;

        &:hover {
          color: #ef4444;
        }
      }
    }
  }
}

/* 侧边栏动画 */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}

/* 图标样式 */
[class^="icon-"],
[class*=" icon-"] {
  display: inline-block;
  width: 100px;
  height: 100px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.icon-clear {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E");
}

.icon-close {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E");
}

.icon-maximize {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z'/%3E%3C/svg%3E");
}

.icon-minimize {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z'/%3E%3C/svg%3E");
}

.icon-send {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M2.01 21L23 12 2.01 3 2 10l15 2-15 2z'/%3E%3C/svg%3E");
}

.icon-send-disabled {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M2.01 21L23 12 2.01 3 2 10l15 2-15 2z'/%3E%3C/svg%3E");
}

.icon-attachment {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z'/%3E%3C/svg%3E");
}

.icon-emoji {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5.5-9c.83 0 1.5-.67 1.5-1.5S7.33 8 6.5 8 5 8.67 5 9.5 5.67 11 6.5 11zm11 0c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5zM8.9 15.55c-.23-.39-.08-.91.31-1.14.39-.23.91-.08 1.14.31.9 1.54 2.59 2.5 4.45 2.5 1.86 0 3.55-.96 4.45-2.5.23-.39.75-.54 1.14-.31.39.23.54.75.31 1.14-1.17 1.98-3.32 3.2-5.7 3.2s-4.53-1.22-5.7-3.2z'/%3E%3C/svg%3E");
}

.icon-history {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z'/%3E%3C/svg%3E");
}

.icon-upload {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z'/%3E%3C/svg%3E");
}

.icon-error {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ef4444'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z'/%3E%3C/svg%3E");
}

.icon-copy {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z'/%3E%3C/svg%3E");
}

.icon-regenerate {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z'/%3E%3C/svg%3E");
}

.icon-like {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-1.91l-.01-.01L23 10z'/%3E%3C/svg%3E");
}

.icon-like.liked {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ef4444'%3E%3Cpath d='M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-1.91l-.01-.01L23 10z'/%3E%3C/svg%3E");
}

.icon-delete {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E%3Cpath d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'/%3E%3C/svg%3E");
}

.icon-connected {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%234ade80'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/%3E%3C/svg%3E");
}

.icon-disconnected {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23fbbf24'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z'/%3E%3C/svg%3E");
}
/* 响应式设计 */
@media (max-width: 768px) {
  .ai-chat-container {
    height: 93vh;
    max-height: 100vh;
    border-radius: 0;
  }

  .message-content {
    max-width: 85%;
  }

  .history-sidebar {
    width: 50%;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  .ai-chat-container {
    background: linear-gradient(135deg, #1e3a8a 0%, #6b21a8 100%);
  }
}

/* 初始化加载动画样式 */
.initializing-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #FFFFFF;
  border-radius: 80px;
  box-shadow: 20px 20px 30px 30px rgba(221, 221, 221, 0.59);

  .loader-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 100px;

    .loader-icon {
      width: 400px;
      height: 400px;
      border-radius: 50%;
      overflow: hidden;
      animation: pulse 2s ease-in-out infinite;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .loader-text {
      font-size: 80px;
      color: #666;
      font-weight: 500;
    }

    .typing-indicator {
      display: flex;
      align-items: center;
      gap: 30px;

      span {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background: @primary-color;
        animation: typing 1.4s ease-in-out infinite;

        &:nth-child(1) {
          animation-delay: 0s;
        }

        &:nth-child(2) {
          animation-delay: 0.2s;
        }

        &:nth-child(3) {
          animation-delay: 0.4s;
        }
      }
    }
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

/* 变量配置面板样式 */
.variable-config-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #FFFFFF;
  border-radius: 80px;
  overflow: hidden;
  box-shadow: 20px 20px 30px 30px rgba(221, 221, 221, 0.59);

  .config-panel-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 120px;
    overflow-y: auto;

    .config-panel-header {
      display: flex;
      align-items: center;
      margin-bottom: 120px;
      padding-bottom: 80px;
      border-bottom: 5px solid #f0f0f0;

      .header-icon {
        width: 240px;
        height: 240px;
        margin-right: 60px;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .header-title {
        font-size: 90px;
        font-weight: 500;
        color: #333;
      }
    }

    .config-items {
      flex: 1;
      margin-bottom: 120px;
      .config-item {
        margin-bottom: 100px;
        flex: 1;
        display: flex;
        align-items: center;

        &:last-child {
          margin-bottom: 0;
        }

        .label-group {
          margin-right: 40px;

          .label-tag {
            display: inline-block;
            padding: 10px 40px;
            background: #f5f5f5;
            border-radius: 20px;
            font-size: 60px;
            color: #666;
            margin-bottom: 20px;
          }

          .label-main {
            display: flex;
            align-items: center;
            font-size: 70px;
            font-weight: 500;
            color: #333;

            .info-icon {
              margin-left: 20px;
              color: #2d8cf0;
              cursor: pointer;
              font-size: 80px;

              &:hover {
                color: #57a3f3;
              }
            }
          }
        }

        .input-wrapper {
          flex:1;
          /deep/ .ivu-select,
          /deep/ .ivu-input-wrapper {
            width: 100%;
          }
        }
      }
    }

    .config-panel-footer {
      display: flex;
      justify-content: flex-end;
      padding-top: 80px;
      border-top: 5px solid #f0f0f0;

      .button {
        min-width: 600px;
        background-color: @primary-color;
        color: #fff;
        border: none;
        border-radius: 60px;
        padding: 40px 80px;
        font-size: 70px;
        cursor: pointer;
      }
    }
  }
}
.config-item {
  .input-wrapper {
    /deep/ .ivu-select,
    /deep/ .ivu-input-wrapper {
      width: 100%;
      font-size: 70px !important;
    }

    /deep/ .ivu-select-selection,
    /deep/ .ivu-input {
      height: 140px !important;
      line-height: 140px !important;
      font-size: 70px !important;
      border-radius: 20px !important;
      padding: 0 60px !important;
      border: 5px solid #dcdee2;
    }
    /deep/ .ivu-select-input{
      display: inline-block;
      height: 140px;
      line-height: 140px;
      padding: 0 50px 0 50px;
      outline: none;
      border: none;
      box-sizing: border-box;
      color: #515a6e;
      background-color: transparent;
      position: relative;
      cursor: pointer;
      font-size: 70px !important;
    }
    /deep/ .ivu-select-selection:hover,
    /deep/ .ivu-input:hover {
      border-color: #5D3B05;
    }

    /deep/ .ivu-select-selection:focus,
    /deep/ .ivu-input:focus {
      border-color: #5D3B05;
      box-shadow: 0 0 0 10px rgba(93, 59, 5, 0.2);
      outline: none;
    }

    /deep/ .ivu-select-dropdown {
      max-height: 700px !important;
    }

    /deep/ .ivu-select-item {
      height: 140px !important;
      line-height: 140px !important;
      font-size: 70px !important;
      padding: 0 60px !important;
    }

    /deep/ .ivu-select-placeholder,
    /deep/ .ivu-input::placeholder {
      font-size: 60px !important;
      color: #c5c8ce;
      line-height: 140px !important;
    }

    /deep/ .ivu-select-arrow {
      font-size: 70px !important;
      color: #808695;
    }

    /deep/ .ivu-select-selection-clear {
      font-size: 70px !important;
    }
  }
}
</style>

<style lang="less">
// 全局样式：为变量配置面板的 Select 组件设置选中颜色
// 注意：ViewUI 的 Select 下拉框是挂载在 body 上的，所以需要使用全局样式
.ivu-select-dropdown {
  .ivu-select-item-selected {
    color: @primary-color !important;
    background-color: fade(@primary-color, 10%) !important;
    &:hover {
      background-color: fade(@primary-color, 15%) !important;
    }
  }
  .ivu-select-item-focus {
    background-color: fade(@primary-color, 10%) !important;
  }
  .ivu-select-item:hover {
    background-color: fade(@primary-color, 8%) !important;
  }
}
</style>
